/**
 * Orders API — canonical Order domain (Sprint M-orders).
 *
 * Backed by the os-core /orders endpoints, which read from the
 * `orders` table the Order-Aggregator agent populates by dedup'ing
 * inbound events from ATI.SU / 1С / МойСклад / email RFQs.
 *
 * Status state machine mirrors the DB CHECK + the server's
 * _ALLOWED_TRANSITIONS — UI only offers transitions the server
 * accepts so the operator doesn't see a 400 they could have
 * predicted from the listed buttons.
 */

import { api, type RequestOptions } from "./client";


export type OrderStatus =
  | "new"
  | "accepted"
  | "declined"
  | "in_progress"
  | "completed"
  | "cancelled";


/** Same shape backend returns in the `route_from` / `route_to`
 *  columns. lat/lon present when the source reported coords
 *  (ATI loads typically do; some 1С imports don't). */
export interface OrderRoute {
  city?:    string | null;
  region?:  string | null;
  lat?:     number | null;
  lon?:     number | null;
  address?: string | null;
}


export interface OrderSource {
  source:           string;
  external_id?:     string | null;
  first_seen_at?:   string | null;
  last_seen_at?:    string | null;
}


export interface OrderDto {
  id:            string;
  status:        OrderStatus;
  sources:       OrderSource[];
  customer_id:   string | null;
  route_from:    OrderRoute | null;
  route_to:      OrderRoute | null;
  eq_type:       string | null;
  weight_kg:     number | null;
  volume_m3:     number | null;
  loading_date:  string | null;        // ISO date (YYYY-MM-DD)
  price_hint:    number | null;
  currency:      string;
  metadata:      Record<string, unknown>;
  created_at:    string;
  updated_at:    string;
}


export const ordersApi = {
  list(
    opts?: RequestOptions & { status?: OrderStatus; limit?: number },
  ): Promise<OrderDto[]> {
    const params = new URLSearchParams();
    if (opts?.status) params.set("status", opts.status);
    if (opts?.limit)  params.set("limit", String(opts.limit));
    const q = params.toString();
    return api.get<OrderDto[]>(`/orders${q ? `?${q}` : ""}`, opts);
  },

  get(orderId: string, opts?: RequestOptions): Promise<OrderDto> {
    return api.get<OrderDto>(`/orders/${orderId}`, opts);
  },

  /** Transition the order's state. Backend refuses illegal moves
   *  with `illegal_transition:from->to`; UI gates the buttons on
   *  ALLOWED_TRANSITIONS_UI so the call should never fail in
   *  practice unless someone else moved the order concurrently. */
  patchStatus(
    orderId: string, status: OrderStatus, opts?: RequestOptions,
  ): Promise<OrderDto> {
    return api.patch<OrderDto>(`/orders/${orderId}/status`, { status }, opts);
  },
};


/** UI-side mirror of the server's transition map. Keep in sync
 *  with `_ALLOWED_TRANSITIONS` in `api/orders.py`. */
export const ALLOWED_TRANSITIONS_UI: Record<OrderStatus, OrderStatus[]> = {
  new:         ["accepted", "declined", "cancelled"],
  accepted:    ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
  declined:    [],
  completed:   [],
  cancelled:   [],
};
